import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { usePleskDomains } from '@/hooks/data/usePleskDomains';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { formatRelativeTime } from '@/utils/date';
import type { Node } from '@/types/node';

export interface PleskDomainsModalProps {
  /** Node to show domains for; the modal is open while it is set */
  node: Node | null;
  onClose: () => void;
  /** Whether the user may refresh the cache from the panel */
  canRefresh: boolean;
}

/**
 * Domains hosted on a Plesk node (cached on the control plane) with a refresh action
 */
export function PleskDomainsModal({ node, onClose, canRefresh }: PleskDomainsModalProps) {
  const { domains, total, lastCachedAt, isLoading, error, refresh } = usePleskDomains(node?.id ?? null);
  const refreshAction = useAsyncAction(refresh, { errorMessage: 'Failed to refresh domains' });

  const errorMessage = refreshAction.error ?? error;

  return (
    <Modal isOpen={node !== null} onClose={onClose} size="lg">
      <ModalHeader>Plesk Domains - {node?.name}</ModalHeader>
      <div className="nodes-plesk-modal-info">
        <span className="nodes-plesk-modal-count">{total} domains</span>
        {lastCachedAt && (
          <span className="nodes-plesk-modal-cached">Last cached: {formatRelativeTime(lastCachedAt)}</span>
        )}
        {canRefresh && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refreshAction.run()}
            isLoading={refreshAction.isPending()}
          >
            Refresh
          </Button>
        )}
      </div>
      <ModalBody className="nodes-plesk-modal-content">
        {errorMessage && (
          <div className="modal-error" role="alert">
            {errorMessage}
          </div>
        )}
        {isLoading ? (
          <div className="nodes-plesk-modal-loading">Loading domains...</div>
        ) : domains.length === 0 ? (
          <div className="nodes-plesk-modal-empty">
            No domains cached. Click Refresh to fetch domains from Plesk.
          </div>
        ) : (
          <table className="nodes-plesk-domains-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Plesk ID</th>
                <th>Cached</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id}>
                  <td>
                    <code>{domain.name}</code>
                  </td>
                  <td>{domain.plesk_site_id}</td>
                  <td>{formatRelativeTime(domain.cached_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
